// BasketDrawer — `.ra-drawer` from the redesign bundle.
// design ref: styles.css:2796–2960, bulk-request.jsx:220–353
//
// Right-hand drawer that appears as soon as the basket has at least one item.
// Top: scrollable list of per-recipient groups, each with a collapsible header
// and a BasketTable. Bottom: bulk duration picker + "Customise individual
// resources" toggle. `overlay` (the passkey signing panel after a submit made
// drafts) covers it all: the basket fades behind and is out of reach.

import { Box, Flex, Icon, VStack } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { type ReactNode, useMemo, useState } from "react";
import { TbChevronDown, TbChevronUp, TbClock } from "react-icons/tb";
import Avatar from "components/ds/Avatar";
import GroupAvatar from "components/ds/GroupAvatar";
import DurationChipGroup, {
  type DurationPreset,
} from "components/ds/DurationChipGroup";
import Switch from "components/ds/Switch";
import { durationLabel } from "components/ds/durationLabel";
import type { DurationValue } from "components/ds/DurationField";
import { pluralise } from "modules/utils";
import BasketTable, {
  type BasketItemCfg,
  type BasketTableLayout,
} from "./BasketTable";
import ReasonField from "./ReasonField";
import ReasonScopeSelector from "./ReasonScopeSelector";
import { useContainerWidth } from "./useContainerWidth";
import { useInert } from "./useInert";
import { EVERY_RECIPIENT, type FlatRequestItem } from "./types";
import type { IdentitySelection } from "./useIdentitySelection";
import {
  perItemKey,
  recipientRequestFor,
  resolveItemCfg,
  resolveAccountDuration,
  resolveRoleDuration,
  type ItemCfgContext,
  type RecipientRowStatus,
} from "./payloads";
import {
  allResourcesAllowIndefinite,
  basketIndefiniteOnly,
  capDurationPresets,
} from "./durationLimits";
import RolePicker from "components/RolePicker";
import { catalogLinkName } from "state/members/access_v2/catalogNames";
import {
  requestableRoles,
  selectedRoleIds,
  type RequestTerms,
  type RoleSelection,
} from "./roleSelection";
import type { MFRequestableRole } from "state/members/access_v2/catalogs";

export interface BasketRecipient {
  id: string;
  name: string;
  /** "(Myself)" suffix style — caller controls. */
  suffix?: string;
  tone?: string;
  avatarSrc?: string;
  /** When true, render the GroupAvatar (+N tile) instead of a person Avatar. */
  isGroup?: boolean;
  /** For groups: number of members behind this chip (shown as the suffix). */
  groupCount?: number;
}

// Post-resolution recipient — a BasketRecipient with `memberIds` flattened
// out. Person recipients have `memberIds: [theirId]`; groups expand to all
// their (pre-intersected) member ids. The submit helper iterates over this
// shape to build one CreateAccessRequestInput per (memberId × item).
export interface ResolvedRecipient extends BasketRecipient {
  memberIds: string[];
}

interface BasketDrawerProps {
  /** Condensed tier (below xl, or viewport ≤700px tall): the duration config
   *  docks as a small summary strip and slides up into a full card on
   *  hover/focus, giving the list the drawer's full height. */
  condensed?: boolean;
  recipients: BasketRecipient[];
  items: FlatRequestItem[];
  /** Bulk duration applied to every item when perItem=false. */
  bulkDuration: DurationValue;
  onBulkDurationChange: (next: DurationValue) => void;
  bulkReason: string;
  onBulkReasonChange: (next: string) => void;
  perItem: boolean;
  onPerItemChange: (next: boolean) => void;
  /** Map of `${recipId}__${itemId}` → per-item override (only used when perItem=true). */
  perItemCfg: Record<string, BasketItemCfg>;
  onPerItemCfgPatch: (
    recipientId: string,
    itemId: string,
    patch: Partial<BasketItemCfg>,
  ) => void;
  perAccountDuration: Record<string, DurationValue>;
  onPerAccountDurationPatch: (
    recipientId: string,
    itemId: string,
    identityId: string,
    duration: DurationValue,
  ) => void;
  /** `${recipId}__${resourceId}__${roleId}` → per-role duration override. */
  perRoleDuration: Record<string, DurationValue>;
  onPerRoleDurationPatch: (
    recipientId: string,
    item: FlatRequestItem,
    roleId: string,
    duration: DurationValue,
  ) => void;
  onRemoveItem: (itemId: string) => void;
  rolesFor: (recipientId: string) => RoleSelection;
  /** `everyone` toggles it on the picker every recipient shares. */
  onToggleRole: (
    recipientId: string,
    item: FlatRequestItem,
    roleId: string,
    everyone?: boolean,
  ) => void;
  /** The row as a recipient sees it: their roles and terms. null when nobody
   *  it covers can request it. */
  recipientItem: (
    recipientId: string,
    item: FlatRequestItem,
  ) => FlatRequestItem | null;
  sharesRoles: (item: FlatRequestItem) => boolean;
  /** The terms of every (member × row) the request covers. */
  basketTerms: RequestTerms[];
  /** perItemKey(recipientId, itemId) → whether and for whom the row won't be
   *  sent. Each recipient's BasketTable shows its own. */
  rowStatus?: Record<string, RecipientRowStatus>;
  /** ≥1 selected resource requires a reason — the bulk reason field becomes
   *  mandatory (only relevant in bulk mode; per-item rows are handled in
   *  BasketTable). */
  reasonRequired?: boolean;
  /** How many submittable resources require a reason (for the info bar +
   *  scope selector copy). */
  reasonRequiredCount?: number;
  /** Total submittable resources (the scope selector's "all N" option). */
  submittableCount?: number;
  /** Bulk reason scope: only the required resources (default) vs all. */
  reasonOnlyRequired?: boolean;
  onReasonOnlyRequiredChange?: (next: boolean) => void;
  /** Inline account picker state (PLO-4704). Applied to the self recipient's
   *  table only; other recipients use the backend default. */
  identity?: IdentitySelection;
  /** Shown over the basket, which fades behind it and is out of reach. */
  overlay?: ReactNode;
}

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;

const DURATION_PRESETS: DurationPreset[] = [
  { label: "4 hours", value: { num: 4, unit: "hours" } },
  { label: "1 day", value: { num: 1, unit: "days" } },
  { label: "1 week", value: { num: 1, unit: "weeks" } },
  { label: "1 month", value: { num: 1, unit: "months" } },
  { label: "3 months", value: { num: 3, unit: "months" } },
];

// Height of the docked config strip in the condensed tier.
const CONFIG_STRIP_H = 52;

const BasketDrawer = ({
  condensed = false,
  recipients,
  items,
  bulkDuration,
  onBulkDurationChange,
  bulkReason,
  onBulkReasonChange,
  perItem,
  onPerItemChange,
  perItemCfg,
  onPerItemCfgPatch,
  perAccountDuration,
  onPerAccountDurationPatch,
  perRoleDuration,
  onPerRoleDurationPatch,
  onRemoveItem,
  rolesFor,
  onToggleRole,
  recipientItem,
  sharesRoles,
  basketTerms: resources,
  rowStatus = {},
  reasonRequired = false,
  reasonRequiredCount = 0,
  submittableCount = 0,
  reasonOnlyRequired = true,
  onReasonOnlyRequiredChange,
  identity,
  overlay,
}: BasketDrawerProps) => {
  // The basket stays mounted under the overlay, so it comes back as it was.
  const basketRef = useInert<HTMLDivElement>(Boolean(overlay));
  const [bulkReasonTouched, setBulkReasonTouched] = useState(false);
  // The info bar signals "required" up front; the red error only appears once
  // the user has left the field empty, not on open.
  const showBulkReasonError =
    reasonRequired && bulkReasonTouched && bulkReason.trim() === "";
  // The bulk reason reaches only required resources (default) or all of them.
  const reasonScopeCount = reasonOnlyRequired
    ? reasonRequiredCount
    : submittableCount;
  // Suggest only durations within the basket's most-restrictive cap (plus the
  // cap itself) — longer values stay reachable via Custom, where the per-row
  // warnings flag them.
  const soleRecipient =
    recipients.length === 1 && !recipients[0].isGroup
      ? recipients[0]
      : undefined;
  const durationPresets = useMemo(
    () => capDurationPresets(DURATION_PRESETS, resources),
    [resources],
  );
  // BasketTable density from the list's own measured width: full 4-col grid
  // ≥480, merged compact grid 300–480, card format under 300.
  const { ref: listRef, width: listW } = useContainerWidth<HTMLDivElement>();
  const tableLayout: BasketTableLayout =
    listW <= 0 || listW >= 480 ? "full" : listW >= 300 ? "compact" : "card";

  // Condensed config card: starts EXPANDED (defaultOpen) so the controls are
  // discoverable without interaction; after the user manually collapses it
  // once, it behaves as a hover/focus disclosure — raised while hovered or
  // while focus is inside it (hover alone would collapse under a
  // keyboard/touch user mid-edit). `suppressed` is the manual override:
  // clicking the strip while raised collapses the card even though the
  // pointer/focus still sit on it (they necessarily do — the click put them
  // there). A fresh hover-in re-arms it; so does hover+focus both leaving.
  const [configDefaultOpen, setConfigDefaultOpen] = useState(true);
  const [configHovered, setConfigHovered] = useState(false);
  const [configFocused, setConfigFocused] = useState(false);
  const [configSuppressed, setConfigSuppressed] = useState(false);
  const configRaised =
    (configDefaultOpen || configHovered || configFocused) && !configSuppressed;
  const needsBulkReason = !perItem && reasonRequired && bulkReason.trim() === "";
  const cfgContext: ItemCfgContext = {
    perItem,
    perItemCfg,
    bulkDuration,
    bulkReason,
    perAccountDuration,
    perRoleDuration,
  };
  const durationSummary = perItem
    ? "Per resource"
    : durationLabel(bulkDuration);

  // .ra-drawer__scroll — the per-recipient groups. In the condensed tier the
  // bottom padding clears the docked config strip.
  const recipientList = (
    <VStack
      ref={listRef}
      flex="1"
      // Floor from PLO-5503: header + ~2 rows stay visible on short viewports
      // instead of the config section squeezing the list to nothing.
      minH="176px"
      overflowY="auto"
      align="stretch"
      spacing="18px"
      px="20px"
      pt="18px"
      pb={condensed ? `${CONFIG_STRIP_H + 12}px` : "8px"}
      minW={0}
    >
      {items.filter(sharesRoles).map((item) => {
        const shownFor =
          recipients.find((r) => recipientItem(r.id, item))?.id ??
          EVERY_RECIPIENT;
        const view = recipientItem(shownFor, item);
        const roles = view && requestableRoles(view);
        return view && roles ? (
          <RolePicker
            key={item.id}
            roles={roles}
            selectedIds={selectedRoleIds(view, rolesFor(shownFor))}
            onToggle={(roleId) => onToggleRole(shownFor, item, roleId, true)}
            showHolding={Boolean(soleRecipient)}
            requestFor={
              soleRecipient
                ? recipientRequestFor(soleRecipient, identity?.selfRecipientId)
                : "them"
            }
            label={`Roles of ${catalogLinkName(item.resource)}`}
          />
        ) : null;
      })}
      {recipients.map((r) => (
        <RecipientGroup
          key={r.id}
          recipient={r}
          items={items.map((item) => recipientItem(r.id, item) ?? item)}
          rolePickers={items
            .filter((item) => !sharesRoles(item))
            .map((item) => {
              const view = recipientItem(r.id, item);
              const roles = view && requestableRoles(view);
              return view && roles ? (
                <RolePicker
                  key={item.id}
                  roles={roles}
                  selectedIds={selectedRoleIds(view, rolesFor(r.id))}
                  onToggle={(roleId) => onToggleRole(r.id, item, roleId)}
                  showHolding={!r.isGroup}
                  requestFor={recipientRequestFor(
                    r,
                    identity?.selfRecipientId,
                  )}
                  label={`Roles of ${catalogLinkName(item.resource)}`}
                />
              ) : null;
            })}
          perItem={perItem}
          layout={tableLayout}
          getCfg={(iid) => resolveItemCfg(r.id, iid, cfgContext)}
          patchCfg={(iid, patch) => onPerItemCfgPatch(r.id, iid, patch)}
          getAccountDuration={(iid, aid) =>
            resolveAccountDuration(r.id, iid, aid, cfgContext)
          }
          patchAccountDuration={(iid, aid, d) =>
            onPerAccountDurationPatch(r.id, iid, aid, d)
          }
          getRoleDuration={(item, role, aid) =>
            resolveRoleDuration(r.id, item, role, aid, cfgContext)
          }
          patchRoleDuration={(item, roleId, d) =>
            onPerRoleDurationPatch(r.id, item, roleId, d)
          }
          onRemove={onRemoveItem}
          roleSelection={rolesFor(r.id)}
          rowWarnings={statusSlice(rowStatus, r.id, items, "warning")}
          rowNotes={statusSlice(rowStatus, r.id, items, "note")}
          identity={identity}
        />
      ))}
    </VStack>
  );

  // .ra-drawer__bottom contents — bulk duration + reason + customise toggle.
  const configContents = (
    <>
        {!perItem && (
          <Box display="flex" flexDirection="column">
            {/* The condensed tier's strip already says "Access duration" —
                don't repeat it inside the card. */}
            {!condensed && (
              <Box
                fontFamily="body"
                fontSize="md"
                fontWeight={600}
                color="neutral.950"
              >
                Access duration
              </Box>
            )}
            <Box fontSize="xs" color="neutral.600" mt="2px">
              Applied to every item in this request
            </Box>
            <Box mt="md">
              <DurationChipGroup
                presets={durationPresets}
                value={bulkDuration}
                onChange={onBulkDurationChange}
                // Offer Indefinite only when every selected resource allows it
                // (see allResourcesAllowIndefinite). Mixed baskets pick
                // indefinite per-row via "Customise individual resources".
                allowIndefinite={allResourcesAllowIndefinite(resources)}
                allowCustom={!basketIndefiniteOnly(resources)}
              />
            </Box>
            <Box mt="md">
              <ReasonField
                required={reasonRequired}
                error={showBulkReasonError}
                value={bulkReason}
                onChange={onBulkReasonChange}
                onBlur={() => setBulkReasonTouched(true)}
                rows={3}
                barText={`${reasonRequiredCount} ${pluralise(
                  "resource",
                  reasonRequiredCount,
                )} in this request require a reason for the approver.`}
                placeholder={
                  reasonRequired
                    ? `Provide bulk reasoning to ${reasonScopeCount} ${pluralise(
                        "resource",
                        reasonScopeCount,
                      )}`
                    : "Add context for the approver (optional)"
                }
              />
              {reasonRequired &&
                reasonRequiredCount < submittableCount &&
                onReasonOnlyRequiredChange && (
                  <ReasonScopeSelector
                    onlyRequired={reasonOnlyRequired}
                    onChange={onReasonOnlyRequiredChange}
                    requiredCount={reasonRequiredCount}
                    totalCount={submittableCount}
                  />
                )}
            </Box>
          </Box>
        )}

        {/* .ra-toggle-row */}
        <Flex align="center" gap="10px" pt="2xs">
          <Switch
            checked={perItem}
            onChange={onPerItemChange}
            aria-label="Customise individual resources"
          />
          <Box fontSize="md" fontWeight={500} color="neutral.900">
            Customise individual resources
          </Box>
        </Flex>
    </>
  );

  return (
    <Flex
      as="aside"
      flex="2"
      minW="360px"
      direction="column"
      position="relative"
      borderLeftWidth="1px"
      borderLeftStyle="solid"
      borderLeftColor="neutral.200"
      bg="neutral.0"
      overflow="hidden"
      minH={0}
    >
      {/* display: contents keeps the drawer's own flex layout. */}
      <Box ref={basketRef} display="contents">
        {recipientList}

        {condensed ? (
          /* Condensed config: a docked summary strip; the full card slides up
             over the list on hover or while focus is inside it.
             Motion — purpose: progressive disclosure (the list keeps the
             drawer's height; details surface on intent). Slide, not fade, so
             the card visibly originates from its docked strip (continuity).
             Reduced motion: the transition is disabled and it snaps. */
          <Box
            position="absolute"
            left="0"
            right="0"
            bottom="0"
            zIndex={5}
            bg="neutral.0"
            borderTopWidth="1px"
            borderTopStyle="solid"
            borderTopColor={configRaised ? "neutral.200" : "neutral.100"}
            boxShadow={configRaised ? "overlay" : "none"}
            transform={
              configRaised
                ? "translateY(0)"
                : `translateY(calc(100% - ${CONFIG_STRIP_H}px))`
            }
            transition="transform 220ms cubic-bezier(0.23, 1, 0.32, 1), box-shadow 220ms cubic-bezier(0.23, 1, 0.32, 1)"
            sx={{
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
            onMouseEnter={() => {
              setConfigHovered(true);
              // A fresh hover-in always re-arms the affordance. The click-to-
              // collapse case never hits this — the pointer was already inside.
              setConfigSuppressed(false);
            }}
            onMouseLeave={() => setConfigHovered(false)}
            onFocus={() => setConfigFocused(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setConfigFocused(false);
                // Focus gone and pointer elsewhere → nothing holds the card;
                // clear the override so the next hover/focus raises it again.
                if (!configHovered) setConfigSuppressed(false);
              }
            }}
          >
            {/* Docked strip — a real button so keyboard/touch can raise the
                card without relying on hover. */}
            <Flex
              as="button"
              type="button"
              aria-expanded={configRaised}
              onClick={() => {
                // First manual collapse also ends the expanded-by-default state
                // — from then on it's a plain hover/focus disclosure.
                if (configRaised) setConfigDefaultOpen(false);
                setConfigSuppressed(configRaised);
              }}
              align="center"
              gap="sm"
              w="full"
              h={`${CONFIG_STRIP_H}px`}
              px="20px"
              bg="transparent"
              border="none"
              cursor="pointer"
              textAlign="left"
              fontFamily="inherit"
              _focusVisible={{ outline: "none", boxShadow: "focusRing" }}
            >
              <Box fontSize="md" fontWeight={600} color="neutral.950">
                Access duration
              </Box>
              {needsBulkReason && (
                <Box fontSize="xs" fontWeight={500} color="warning.700">
                  Reason required
                </Box>
              )}
              <Flex
                ml="auto"
                align="center"
                gap="1xs"
                fontSize="smd"
                color="neutral.700"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                <Icon as={TbClock} boxSize="14px" color="neutral.600" />
                {durationSummary}
              </Flex>
              <Icon
                as={TbChevronUp}
                boxSize="16px"
                color="neutral.600"
                transform={configRaised ? "rotate(180deg)" : "none"}
                transition="transform 220ms cubic-bezier(0.23, 1, 0.32, 1)"
                sx={{
                  "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                  },
                }}
              />
            </Flex>
            <Box
              px="20px"
              pb="20px"
              display="flex"
              flexDirection="column"
              gap="14px"
            >
              {configContents}
            </Box>
          </Box>
        ) : (
          /* .ra-drawer__bottom — always-open config under the list. Scrolls
             itself when there isn't room (PLO-5503) rather than pushing the
             resource list out of the panel. */
          <Box
            borderTopWidth="1px"
            borderTopStyle="solid"
            borderTopColor="neutral.100"
            px="20px"
            pt="lg" // 16px
            pb="20px"
            bg="neutral.0"
            display="flex"
            flexDirection="column"
            gap="14px"
            minH={0}
            overflowY="auto"
          >
            {configContents}
          </Box>
        )}
      </Box>

      {overlay && (
        /* Fade, not slide: the panel takes over the drawer in place.
           Reduced motion: it appears without the fade. */
        <Flex
          position="absolute"
          inset={0}
          zIndex={10}
          direction="column"
          align="center"
          px="20px"
          py="1xl"
          overflowY="auto"
          bg="rgba(252, 253, 253, 0.9)"
          animation={`${fadeIn} 160ms cubic-bezier(0.23, 1, 0.32, 1)`}
          sx={{
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {/* Auto margins centre it vertically, and let it scroll when tall. */}
          <Box w="full" maxW="400px" my="auto">
            {overlay}
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

const statusSlice = (
  rowStatus: Record<string, RecipientRowStatus>,
  recipientId: string,
  items: FlatRequestItem[],
  field: "warning" | "note",
): Record<string, string | undefined> =>
  Object.fromEntries(
    items.map((item) => [
      item.id,
      rowStatus[perItemKey(recipientId, item.id)]?.[field],
    ]),
  );

const RecipientGroup = ({
  recipient,
  items,
  rolePickers,
  perItem,
  layout,
  getCfg,
  patchCfg,
  getAccountDuration,
  patchAccountDuration,
  getRoleDuration,
  patchRoleDuration,
  onRemove,
  roleSelection,
  rowWarnings,
  rowNotes,
  identity,
}: {
  recipient: BasketRecipient;
  items: FlatRequestItem[];
  rolePickers: ReactNode[];
  perItem: boolean;
  layout: BasketTableLayout;
  getCfg: (itemId: string) => BasketItemCfg;
  patchCfg: (itemId: string, patch: Partial<BasketItemCfg>) => void;
  getAccountDuration: (itemId: string, identityId: string) => DurationValue;
  patchAccountDuration: (
    itemId: string,
    identityId: string,
    duration: DurationValue,
  ) => void;
  getRoleDuration: (
    item: FlatRequestItem,
    role: MFRequestableRole,
    identityId?: string,
  ) => DurationValue;
  patchRoleDuration: (
    item: FlatRequestItem,
    roleId: string,
    duration: DurationValue,
  ) => void;
  onRemove: (itemId: string) => void;
  roleSelection: RoleSelection;
  rowWarnings?: Record<string, string | undefined>;
  rowNotes?: Record<string, string | undefined>;
  identity?: IdentitySelection;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  // The account picker only applies to the requester's own rows — we only hold
  // the current user's identities. Groups/subordinates use the backend default.
  const identityForTable =
    identity && identity.selfRecipientId === recipient.id
      ? {
          getOptions: identity.getOptions,
          getSelected: identity.getSelected,
          onToggle: identity.toggle,
          problem: identity.problem,
        }
      : undefined;
  return (
    <Box display="flex" flexDirection="column" gap="md">
      <Box
        as="button"
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        display="flex"
        alignItems="center"
        gap="10px"
        w="full"
        px="2xs"
        py="2xs"
        bg="transparent"
        border="none"
        cursor="pointer"
        fontFamily="inherit"
        textAlign="left"
      >
        {recipient.isGroup ? (
          <GroupAvatar count={recipient.groupCount ?? 0} size="sm" />
        ) : (
          <Avatar
            name={recipient.name}
            src={recipient.avatarSrc}
            tone={recipient.tone}
            size="sm"
            ring
          />
        )}
        <Box as="span" fontSize="lg" fontWeight={700} color="neutral.950">
          {recipient.name}
        </Box>
        {recipient.suffix && (
          <Box as="span" fontSize="lg" color="neutral.600" fontWeight={400}>
            {recipient.suffix}
          </Box>
        )}
        <Icon
          as={collapsed ? TbChevronDown : TbChevronUp}
          boxSize="18px"
          color="neutral.600"
          ml="auto"
        />
      </Box>
      {!collapsed && rolePickers}
      {!collapsed && (
        <BasketTable
          items={items}
          perItem={perItem}
          layout={layout}
          getCfg={getCfg}
          patchCfg={patchCfg}
          getAccountDuration={getAccountDuration}
          patchAccountDuration={patchAccountDuration}
          getRoleDuration={getRoleDuration}
          patchRoleDuration={patchRoleDuration}
          onRemove={onRemove}
          roleSelection={roleSelection}
          rowWarnings={rowWarnings}
          rowNotes={rowNotes}
          identity={identityForTable}
        />
      )}
    </Box>
  );
};

export default BasketDrawer;
